// ════════════════════════════════════════════════════════════
// GastosTab — gastos de la intervención (tickets y facturas del técnico)
// El técnico los sube; administración o su responsable los valida.
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Chip, Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  CircularProgress, InputAdornment, FormControlLabel, Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getTiposGasto, getGastosIntervencion, crearGastoIntervencion, validarGasto,
  rechazarGasto, borrarGastoIntervencion, descargarTicketGasto
} from '../api';

const ESTADOS: any = {
  pendiente: { label: 'Pendiente de validar', color: 'warning' },
  validado:  { label: 'Validado',             color: 'success' },
  rechazado: { label: 'Rechazado',            color: 'error' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function GastosTab({ intervencion, onActualizado }: any) {
  const [tipos, setTipos] = useState<any[]>([]);
  const [datos, setDatos] = useState<any>({ items: [], totales: {}, puede_validar: false });
  const [cargando, setCargando] = useState(true);
  const [nuevo, setNuevo] = useState<any>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [guardando, setGuardando] = useState(false);

  const usuario = JSON.parse(localStorage.getItem('user') || '{}');
  const cerrada = intervencion?.estado === 'completada';

  const cargar = () => {
    setCargando(true);
    getGastosIntervencion(intervencion.id)
      .then(setDatos).catch(() => setDatos({ items: [], totales: {}, puede_validar: false }))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    getTiposGasto().then(setTipos).catch(() => setTipos([]));
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervencion.id]);

  const abrirNuevo = () => {
    setArchivo(null);
    setNuevo({
      fecha: new Date().toISOString().slice(0, 10), tipo: 'peaje', concepto: '',
      proveedor: '', importe: '', iva: '', pagado_por: 'empresa', notas: '',
    });
  };

  const guardar = async () => {
    if (!nuevo.concepto.trim()) { alert('Indica el concepto del gasto'); return; }
    if (!(parseFloat(nuevo.importe) > 0)) { alert('Indica el importe'); return; }
    setGuardando(true);
    try {
      await crearGastoIntervencion(intervencion.id, {
        ...nuevo, importe: parseFloat(nuevo.importe) || 0, iva: parseFloat(nuevo.iva) || 0,
      }, archivo);
      setNuevo(null);
      setArchivo(null);
      cargar();
      if (onActualizado) onActualizado();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al registrar el gasto');
    } finally {
      setGuardando(false);
    }
  };

  const validar = async (g: any) => {
    if (!window.confirm(
      `¿Validar el gasto de ${eur(g.importe)} (${g.concepto})?\n\n` +
      `Se imputará al cost control de la máquina` +
      (g.pagado_por === 'empresa' ? ' y aparecerá en Administración como factura a pagar.' : '.'))) return;
    try {
      const r = await validarGasto(g.id);
      cargar();
      if (onActualizado) onActualizado();
      alert(`✅ ${r.mensaje}`);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al validar');
    }
  };

  const rechazar = async (g: any) => {
    const motivo = window.prompt(`Rechazar el gasto «${g.concepto}». Motivo:`);
    if (!motivo) return;
    try { await rechazarGasto(g.id, motivo); cargar(); if (onActualizado) onActualizado(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al rechazar'); }
  };

  const borrar = async (g: any) => {
    if (!window.confirm(`¿Eliminar el gasto «${g.concepto}»?`)) return;
    try { await borrarGastoIntervencion(g.id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const t = datos.totales || {};

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #f59e0b' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pendientes de validar</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                {t.n_pendientes || 0} · {eur(t.pendiente)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card sx={{ borderRadius: 3, borderLeft: '4px solid #16a34a' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Validados (van al cost control)</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a' }}>{eur(t.validado)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
          {!cerrada && (
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
              sx={{ bgcolor: '#4f46e5', py: 1.2 }}>
              Añadir gasto
            </Button>
          )}
        </Grid>
      </Grid>

{cargando ? <CircularProgress /> : datos.items.length === 0 ? (
        <Alert severity="info">
          Todavía no hay gastos registrados. Sube aquí los tickets de peajes, hoteles,
          comidas o cualquier compra hecha durante la intervención.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Técnico</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Pagado por</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datos.items.map((g: any) => {
                const est = ESTADOS[g.estado] || { label: g.estado, color: 'default' };
                const propio = g.tecnico_id === usuario.id;
                return (
                  <TableRow key={g.id} hover>
                    <TableCell>{g.fecha}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.concepto}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tipos.find((x: any) => x.clave === g.tipo)?.nombre || g.tipo}
                        {g.proveedor ? ` · ${g.proveedor}` : ''}
                      </Typography>
                      {g.motivo_rechazo && (
                        <Typography variant="caption" color="error" display="block">
                          Rechazado: {g.motivo_rechazo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="caption">
                        {g.tecnico_nombre} {g.tecnico_apellidos || ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(g.importe)}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                        label={g.pagado_por === 'tecnico' ? 'El técnico' : 'La empresa'} />
                    </TableCell>
                    <TableCell><Chip size="small" label={est.label} color={est.color} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {g.archivo && (
                        <IconButton size="small" title="Ver ticket"
                          onClick={() => descargarTicketGasto(g.id, g.archivo_nombre)}>
                          📎
                        </IconButton>
                      )}
                      {datos.puede_validar && g.estado !== 'validado' && (
                        <Button size="small" variant="contained" sx={{ bgcolor: '#22c55e', mr: 0.5 }}
                          onClick={() => validar(g)}>
                          Validar
                        </Button>
                      )}
                      {datos.puede_validar && g.estado !== 'rechazado' && (
                        <Button size="small" color="error" onClick={() => rechazar(g)}>
                          Rechazar
                        </Button>
                      )}
                      {propio && g.estado === 'pendiente' && !datos.puede_validar && (
                        <IconButton size="small" color="error" onClick={() => borrar(g)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* NUEVO GASTO */}
      <Dialog open={!!nuevo} maxWidth="sm" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setNuevo(null); }}>
        <DialogTitle>💳 Nuevo gasto de la intervención</DialogTitle>
        <DialogContent>
          {nuevo && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Fecha"
                  InputLabelProps={{ shrink: true }} value={nuevo.fecha}
                  onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select value={nuevo.tipo} label="Tipo"
                    onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value })}>
                    {tipos.map((x: any) => <MenuItem key={x.clave} value={x.clave}>{x.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Concepto *" value={nuevo.concepto}
                  placeholder="Ej: peaje AP-7 ida y vuelta"
                  onChange={(e) => setNuevo({ ...nuevo, concepto: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Proveedor / establecimiento"
                  value={nuevo.proveedor}
                  onChange={(e) => setNuevo({ ...nuevo, proveedor: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="Importe total *"
                  value={nuevo.importe}
                  onChange={(e) => setNuevo({ ...nuevo, importe: e.target.value })}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="IVA incluido"
                  value={nuevo.iva}
                  onChange={(e) => setNuevo({ ...nuevo, iva: e.target.value })}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={nuevo.pagado_por === 'tecnico'}
                    onChange={(e) => setNuevo({ ...nuevo, pagado_por: e.target.checked ? 'tecnico' : 'empresa' })} />}
                  label={nuevo.pagado_por === 'tecnico'
                    ? 'Lo he pagado yo (para reembolso)'
                    : 'Lo paga la empresa (tarjeta, a crédito...)'} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  {archivo ? `📎 ${archivo.name}` : '📷 Adjuntar ticket o factura (foto o PDF)'}
                  <input type="file" hidden accept=".pdf,image/*" capture="environment"
                    onChange={(e) => setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notas (opcional)" value={nuevo.notas}
                  onChange={(e) => setNuevo({ ...nuevo, notas: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevo(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando} sx={{ bgcolor: '#4f46e5' }}>
            {guardando ? 'Guardando...' : 'Registrar gasto'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
